import React, { useEffect, useState, useRef, useCallback } from "react";
import { MapContainer, TileLayer, GeoJSON, LayersControl  } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import proj4 from "proj4";
import { FaCircleInfo } from "react-icons/fa6";
import { MapHomeControl, MapLegendControl } from "../../../../common/MapControls/MapControls";

const INITIAL_VIEW_STATE = {
  longitude: 125.7275,
  latitude: -8.8742,
  zoom: 8,
};
const InfoButton = ({ onClick }) => {
  // const map = useMap();
  // Leaflet's own drag/click handling swallows clicks on overlay controls
  // unless propagation to the map is explicitly disabled on the DOM node.
  const btnRef = useCallback((node) => {
    if (node) {
      L.DomEvent.disableClickPropagation(node);
      L.DomEvent.disableScrollPropagation(node);
    }
  }, []);
  return (
    <div
      ref={btnRef}
      style={{
        position: "absolute",
        bottom: "20px",
        left: "12px",
        padding: "8px",
        borderRadius: "8px",
        fontSize: "15px",
        //boxShadow: "0 0 6px rgba(0,0,0,0.3)",
        cursor: "pointer",
        background: "rgba(255, 255, 255, 0.9)",
        backdropFilter: "blur(10px) brightness(1.1)",
        WebkitBackdropFilter: "blur(10px) brightness(1.1)",
        border: "1px solid rgba(0, 0, 0, 0.08)",
        color: "#333333",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        transition: "all 0.2s ease",
        zIndex: 1000,
      }}
      onClick={onClick}
    >
      <FaCircleInfo />
    </div>
  );
};
// Define UTM Zone 51S projection and WGS84 projection
const UTM_51S = "+proj=utm +zone=51 +south +datum=WGS84 +units=m +no_defs";
const WGS84 = "EPSG:4326";

// Function to reproject a GeoJSON object
const reprojectGeoJSON = (geojson) => {
  if (!geojson || !geojson.features) return geojson;

  return {
    ...geojson,
    features: geojson.features.map((feature) => {
      const { geometry } = feature;

      if (geometry.type === "LineString" || geometry.type === "MultiLineString") {
        // Check if coordinates are valid numbers
        const validCoordinates = geometry.coordinates.every(
          coord => Array.isArray(coord) && coord.length === 2 && coord.every(Number.isFinite)
        );

        if (validCoordinates) {
          const reprojectedCoords = geometry.coordinates.map(([x, y]) =>
            proj4(UTM_51S, WGS84, [x, y])
          );
          return {
            ...feature,
            geometry: { ...geometry, coordinates: reprojectedCoords },
          };
        }
      }

      return null; // Filter out invalid features
    }).filter((feature) => feature !== null),
  };
};

const RoadNetwork = () => {
  const [nationalRoads, setNationalRoads] = useState(null);
  const [districtRoads, setDistrictRoads] = useState(null); // State for district roads
  const [geojsonData, setGeojsonData] = useState({
    country: null,
  });
  const [legendVisible, setLegendVisible] = useState(false);
  const legendRef = useRef(null);
    const [info, setInfo] = useState(false);
    const infoRef = useRef(null);
    const infoPanelRef = useCallback((node) => {
      infoRef.current = node;
      if (node) {
        L.DomEvent.disableClickPropagation(node);
        L.DomEvent.disableScrollPropagation(node);
      }
    }, []);
  // Fetch GeoJSON files and reproject them
  useEffect(() => {
    Promise.all([
      fetch("/DATA/Landslide/Exposure/National_Road.json").then((response) => response.json()),
      fetch("/DATA/Landslide/Exposure/District_Road.json").then((response) => response.json()), // Fetch district roads
      fetch("/DATA/OSMbased_adm0_boundary.json").then((response) => response.json()),
    ])
      .then(([nationalData, districtData, country]) => {
        console.log("National Roads Data:", nationalData);
        console.log("District Roads Data:", districtData);
        console.log("GeoJSON Data:", { country });

        setNationalRoads(reprojectGeoJSON(nationalData));
        setDistrictRoads(reprojectGeoJSON(districtData));
        setGeojsonData({ country });
      })
      .catch((error) => {
        console.error("Error loading GeoJSON files:", error);
      });
  }, []);

  // const toggleLegend = () => {
    setLegendVisible(!legendVisible);
  // };

  // Close legend when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (legendRef.current && !legendRef.current.contains(event.target)) {
        setLegendVisible(false);
      }
    };

    if (legendVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [legendVisible]);

  // Style functions for GeoJSON layers
  const nationalRoadStyle = {
    color: "red", // Red for national roads
    weight: 2,
    opacity: 0.8,
  };

  const districtRoadStyle = {
    color: "blue", // Blue for district roads
    weight: 2,
    opacity: 0.8,
  };

  const geojsonStyle = (type) => ({
    color: "#000000", 
    weight: 1,
    opacity: 0.8,
    fillOpacity: 0 
  });

  const onEachFeature = (feature, layer) => {
    if (!geojsonData.country || feature.properties) {
      layer.on({
        click: () => {
          // Display popup with properties of the clicked feature
          const popupContent = Object.keys(feature.properties)
            .map(key => `<b>${key}</b>: ${feature.properties[key]}`)
            .join('<br />');
          layer.bindPopup(popupContent).openPopup();
        },
      });
    }
  };

  return (
    <div className="wind-speed-cyclone-container">
      <div className="map-container-WindSpeedCyclone">
        <MapContainer
          center={[INITIAL_VIEW_STATE.latitude, INITIAL_VIEW_STATE.longitude]}
          zoom={INITIAL_VIEW_STATE.zoom}
          className="mapcontainerall"
        >
          <MapHomeControl />
                              {info && (
            <div
              ref={infoPanelRef}
              style={{
                position: "absolute",
                bottom: "70px",
                left: "12px",
                fontSize: "12px",
                zIndex: 1000,
                padding: "10px 14px",
                borderRadius: "10px",
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(10px) brightness(1.1)",
                WebkitBackdropFilter: "blur(10px) brightness(1.1)",
                border: "1px solid rgba(0, 0, 0, 0.08)",
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.5)",
                color: "#333333",
                fontWeight: "500",
                maxWidth: "320px",
                lineHeight: "1.4",
              }}
            >
              The RoadNetwork information are available here.
            </div>
          )}
          <InfoButton onClick={() => setInfo(!info)} />
          {/* Layer toggle control */}
          <LayersControl position="topright">
            <LayersControl.BaseLayer name="White Theme" checked>
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="OpenStreetMap">
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
              />
            </LayersControl.BaseLayer>

            {/* National Roads Layer */}
            {nationalRoads && (
              <LayersControl.Overlay name="National Roads" checked>
                <GeoJSON data={nationalRoads} style={nationalRoadStyle} onEachFeature={onEachFeature} />
              </LayersControl.Overlay>
            )}

            {/* District Roads Layer */}
            {districtRoads && (
              <LayersControl.Overlay name="District Roads" checked>
                <GeoJSON data={districtRoads} style={districtRoadStyle} onEachFeature={onEachFeature} />
              </LayersControl.Overlay>
            )}

            {/* Country Layer */}
            {geojsonData.country && (
              <LayersControl.Overlay name="Country" checked>
                <GeoJSON data={geojsonData.country} style={geojsonStyle("country")} />
              </LayersControl.Overlay>
            )}
          </LayersControl>

          {/* Legend */}
          <MapLegendControl
            sections={[{
              shape: "square",
              items: [
                { label: "National Roads", color: "red" },
                { label: "District Roads", color: "blue" },
              ],
            }]}
          />
        </MapContainer>
      </div>
    </div>
  );
};

export default RoadNetwork;